import React, { useState, useMemo, useEffect } from "react";
import "./AdminProjects.css";
import bfLogo from "../assets/bf_logo.png";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* ===== FIREBASE ===== */
import { db, auth } from "../firebase";
import { collection, query, where, doc, getDoc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

/* --- Pagalbinė funkcija datos formatavimui --- */
const formatDate = (dateString) => {
  if (!dateString) return "--.--.--";
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (e) {
    return "--.--.--";
  }
};

export default function AdminProjects() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [currentUser, setCurrentUser] = useState(null);

  /* 1. STEBIME PRISIJUNGIMĄ */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        navigate("/admin/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  /* 2. KRAUNAME PROJEKTUS REALIU LAIKU */
  useEffect(() => {
    if (!currentUser) return;

    let unsubscribe = () => {};

    const setupListener = async () => {
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        let masterId = currentUser.uid;
        if (userSnap.exists()) {
          const uData = userSnap.data();
          masterId = uData.belongsTo || currentUser.uid;
        }

        const q = query(
          collection(db, "projects"), 
          where("adminId", "==", masterId)
        );
        
        unsubscribe = onSnapshot(q, (snap) => {
          const projectsList = snap.docs.map(d => {
            const data = d.data();
            
            // Gauname metus
            let itemYear = data.year;
            if (!itemYear && data.createdAt) {
                itemYear = new Date(data.createdAt).getFullYear();
            }
            if (!itemYear) itemYear = new Date().getFullYear();

            return {
              id: d.id, 
              auftraggeber: data.info?.auftraggeber || "N/A",
              objektas: data.info?.bauvorhaben || "N/A",
              kw: Number(data.kw) || 0, 
              year: Number(itemYear),   
              date: data.createdAt || data.updatedAt || null,
            };
          });

          // RŪŠIAVIMAS: Metai (mažėjančiai) -> KW (mažėjančiai)
          const sorted = projectsList.sort((a, b) => {
            if (b.year !== a.year) {
                return b.year - a.year; 
            }
            return b.kw - a.kw;
          });

          setProjects(sorted);
        }, (err) => {
          console.error("Klaida kraunant projektus:", err);
        });

      } catch (err) {
        console.error("Klaida nustatant masterId:", err);
      }
    };

    setupListener();
    return () => unsubscribe();
  }, [currentUser]);

  /* 3. FILTRAVIMAS (DABAR IR PAGAL DATĄ BEI KW) */
  const filtered = useMemo(() => {
    const lowerSearch = search.toLowerCase();

    return projects.filter(p => {
      // Sujungiame viską į vieną tekstą paieškai
      const searchableText = `
        ${p.auftraggeber} 
        ${p.objektas} 
        ${p.kw} 
        kw${p.kw} 
        kw ${p.kw} 
        ${formatDate(p.date)}
      `.toLowerCase();

      return searchableText.includes(lowerSearch);
    });
  }, [projects, search]);

  return (
    <div className="projects-container">
      {/* PATAISYMAS: Pridėtas stilius, kad mygtukas nebūtų prilipęs prie krašto */}
      <button 
        className="back-btn" 
        onClick={() => navigate("/admin")}
        style={{ position: "absolute", top: "30px", left: "30px", zIndex: 100 }}
      >
        ← {t('back')}
      </button>

      <div className="projects-content">
        <img src={bfLogo} alt="BF" className="projects-logo" />
        <h1 className="projects-title">{t('projects')}</h1>

        <div className="projects-topbar">
          <div className="projects-search">
            <Search size={18} />
            <input
              placeholder={t('search') + "..."}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div className="projects-count">
            {filtered.length} {t('projects')}
          </div>
        </div>

        {/* --- SCROLL LIST --- */}
        <div className="projects-scroll-list">
          {filtered.map(p => (
            <div key={p.id} className="project-card">
              <div className="project-info">
                <div className="project-name">{p.auftraggeber}</div>
                <div className="project-meta">{p.objektas}</div>
                <div className="project-date-badge">
                  {formatDate(p.date)} | KW {p.kw}
                </div>
              </div>

              <div className="project-actions">
                <button
                  className="open-btn"
                  onClick={() => {
                    navigate(`/admin/projects/${p.id}/${p.kw}/v1`);
                  }}
                >
                  {t('open') || "Atidaryti"} 
                </button>
              </div>
            </div>
          ))}

          {filtered.length === 0 && (
            <div style={{ textAlign: "center", opacity: 0.6, marginTop: "40px" }}>
              {t('no_projects') || "No projects found"}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}